import { useState, useEffect } from 'react';
import { RemoteControl } from '@limrun/ui';
import { mutate } from 'swr';
import { Limrun } from '@limrun/api';

import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '@/client/backend/user';
import { Sidebar } from '@/components/layout/Sidebar';
import { toast } from '@/components/ui/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader, X, Copy, Cable, ChevronRight, ChevronLeft } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization.tsx";

import { useParams } from 'react-router-dom';
import { Label } from "@/components/ui/label";
import { formatErrorMessage } from '@/utils/format';

export default function StreamPage() {
  const navigate = useNavigate();
  // Get instance name from URL params
  const { instanceId } = useParams<{ instanceId?: string }>();
  const [instance, setInstance] = useState<Limrun.AndroidInstance | Limrun.IosInstance | "loading" | undefined>(undefined);
  const { user, error: userError } = useUser();
  if (userError) {
    console.log({ userError });
  }
  const [organization, setOrganization] = useOrganization();
  const auth = useAuth();

  // Organization IDs that have access to Android 13 Tablet
  const tabletOrganizationIds = [
    "org_01k3v8bvxfecfr8vjqa1jd9rrk",
    "org_01k3v8mbmvfys91nqpym6zx880",
    "org_01k3v8mbmkfysaqkvd1dt1b2tj"
  ];
  const isTabletOrganization = organization !== "loading" && tabletOrganizationIds.includes(organization.tid);

  // State for managing instance creation and connection
  const [isCreating, setIsCreating] = useState(false);
  const [selectedOS, setSelectedOS] = useState<'Android 13 Tablet' | 'Android 14' | 'Android 15' | 'iOS 26'>(
    isTabletOrganization ? 'Android 13 Tablet' : 'Android 14'
  );
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Effect to update selectedOS when organization changes
  useEffect(() => {
    if (!instance) {
      if (isTabletOrganization && selectedOS !== 'Android 13 Tablet') {
        setSelectedOS('Android 13 Tablet');
      } else if (!isTabletOrganization && selectedOS === 'Android 13 Tablet') {
        setSelectedOS('Android 14');
      }
    }
  }, [isTabletOrganization, instance]);

  // Effect to auto-collapse sidebar when streaming starts
  useEffect(() => {
    if (instance !== "loading" && 
        instance?.status?.state === "ready" && 
        instance?.status?.endpointWebSocketUrl) {
      setIsSidebarCollapsed(true);
    }
  }, [instance]);

  // Function to handle instance creation and connection
  const handleCreateAndConnect = async () => {
    if (instance) return; // Don't create if we already have an instance
    try {
      setIsCreating(true);
      if (selectedOS === 'iOS 26') {
        const result = await auth.regionClient.iosInstances.create({ wait: true });
        setInstance(result);
        await mutate('iosInstances');
        navigate(`/stream/${result.metadata.id}`, { replace: true });
      } else {
        // Determine OS version using exact match
        let osVersion: string;
        switch (selectedOS) {
          case 'Android 13 Tablet':
            osVersion = "13_NCR";
            break;
          case 'Android 14':
            osVersion = "14";
            break;
          case 'Android 15':
            osVersion = "15";
            break;
          default:
            throw new Error(`Unknown Android OS version: ${selectedOS}`);
        }
        const result = await auth.regionClient.androidInstances.create({
          wait: true,
          spec: {
            clues: [
              {
                kind: "OSVersion",
                osVersion: osVersion
              }
            ]
          },
        });
        setInstance(result);
        await mutate('androidInstances');
        navigate(`/stream/${result.metadata.id}`, { replace: true });
      }
      toast({
        title: "Instance Ready",
        description: `Your ${selectedOS.startsWith('iOS') ? 'iOS' : 'Android'} instance is ready to use.`,
      });
    } catch (error: any) {
      switch (error.reason) {
        case "NoSubscription":
          toast({
            title: "Uh oh!",
            description: "There is no subscription for this organization.",
            variant: "destructive",
          });
          break;
        case "NoRemainingMinutes":
          toast({
            title: "Uh oh!",
            description: "You have no remaining minutes.",
            variant: "destructive",
          });
          break;
        case "ConcurrencyLimitReached":
          toast({
            title: "Uh oh!",
            description: "You can't create more instances at the same time. Increase the concurrency limit in settings or stop an instance to create a new one.",
            variant: "destructive",
          });
          break;
        default:
          toast({
            title: "Uh oh!",
            description: formatErrorMessage(error.message ?? error.toString()),
            variant: "destructive",
          });
          break;
      }
      setIsCreating(false);
    }
  };

  const handleStopInstance = async () => {
    if (!instance) return;
    if (instance === "loading" && !instanceId) return;
    const idToDelete = instance === "loading" ? instanceId! : instance.metadata.id;
    
    try {
      if (idToDelete.startsWith('ios')) {
        await auth.regionClient.iosInstances.delete(idToDelete);
        await mutate('iosInstances');
      } else {
        await auth.regionClient.androidInstances.delete(idToDelete);
        await mutate('androidInstances');
      }
      setInstance(undefined);
      setIsCreating(false);
      setIsSidebarCollapsed(false);
      toast({
        title: "Instance Stopped",
        description: `Your ${idToDelete.startsWith('ios') ? 'iOS' : 'Android'} instance has been stopped and deleted.`,
      });
      navigate('/stream');
    } catch (error: any) {
      toast({
        title: "Uh oh!",
        description: formatErrorMessage(error.message ?? error.toString()),
        variant: "destructive",
      });
      return;
    }
  };

  useEffect(() => {
    if (!instance && instanceId) {
      const fetchInstance = async () => {
        try {
          const existingInstance = await auth.regionClient[instanceId.startsWith('ios') ? 'iosInstances' : 'androidInstances'].get(instanceId);
          if (existingInstance.status.state === "ready") {
            setInstance(existingInstance);
          }
        } catch (error: any) {
          toast({
            title: "Uh oh!",
            description: formatErrorMessage(error.message ?? error.toString()),
            variant: "destructive",
          });
        }
      };
      fetchInstance();
    }
  }, [instanceId, instance, auth.regionClient]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <Sidebar />
      <div className="flex flex-col sm:gap-4 sm:py-1 sm:pl-14">
        <main className={`flex flex-1 p-4 ${isSidebarCollapsed ? 'gap-0' : 'gap-4'}`}>
          {/* Left Control Panel */}
          <div className={`space-y-4 transition-all duration-300 ${isSidebarCollapsed ? 'w-16' : 'w-80'}`}>
            {isSidebarCollapsed ? (
              /* Collapsed sidebar with icon-only buttons */
              <div className="flex flex-col gap-2">
                {/* Expand button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsSidebarCollapsed(false)}
                  className="h-8 w-8"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
                
                {instance && (
                  <>
                    <Dialog open={isConnectDialogOpen} onOpenChange={setIsConnectDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={instance === "loading" || instance?.status?.state !== "ready"}
                          className="h-8 w-8"
                        >
                          <Cable className="h-3 w-3" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                          <DialogTitle>Connect via CLI</DialogTitle>
                          <DialogDescription>
                            Use the command below to connect to your instance via the CLI
                          </DialogDescription>
                        </DialogHeader>
                        {instance !== "loading" && (
                          <div className="space-y-4">
                          <div className="p-4 bg-muted rounded-md overflow-auto">
                            <pre className="text-sm">
                              <code className="whitespace-pre-wrap break-all">
                                {`lim connect ${selectedOS.startsWith('iOS') ? 'ios' : 'android'} ${instance.metadata.id}`}
                              </code>
                            </pre>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-muted-foreground">
                              If <code className="p-1 rounded-md bg-muted text-xs">lim</code> is not installed, see the instructions in the <Link to="https://limbar.io/docs" target="_blank" className="text-blue-500 hover:underline">docs</Link>.
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const command = `lim connect ${selectedOS.startsWith('iOS') ? 'ios' : 'android'} ${instance.metadata.id}`;
                                navigator.clipboard.writeText(command);
                                toast({
                                  title: "Copied",
                                  description: "Command copied to clipboard",
                                });
                              }}
                            >
                              <Copy className="mr-2 h-3.5 w-3.5" />
                              Copy
                            </Button>
                          </div>
                        </div>
                        )}
                      </DialogContent>
                    </Dialog>
                    
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={handleStopInstance}
                      className="h-8 w-8"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            ) : (
              /* Full sidebar */
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Controls</CardTitle>
                      <CardDescription>
                        Manage your virtual device instance
                      </CardDescription>
                    </div>
                    {instance && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsSidebarCollapsed(true)}
                        className="h-8 w-8"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Organization Selector */}
                  <div className="space-y-2">
                    <Label htmlFor="org-select">Organization</Label>
                    <Select
                      onValueChange={(value) => {
                        const selected =
                          user !== "loading"
                            ? user.organizations.find((org) => org.id === value)
                            : undefined;
                        if (selected) {
                          setOrganization(selected);
                        }
                      }}
                    >
                      <SelectTrigger id="org-select">
                        <SelectValue
                          placeholder={
                            organization === "loading"
                              ? "Loading..."
                              : organization.displayName
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Organizations</SelectLabel>
                          {(user !== "loading" ? user.organizations : []).map(
                            (org) => (
                              <SelectItem value={org.id} key={org.id}>
                                {org.displayName}
                              </SelectItem>
                            ),
                          )}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* OS Selector (only when no instance exists) */}
                  {!instance && (
                    <div className="space-y-2">
                      <Label htmlFor="os-select">Operating System</Label>
                      <Select
                        value={selectedOS}
                        onValueChange={(value: 'Android 13 Tablet' | 'Android 14' | 'Android 15' | 'iOS 26') => setSelectedOS(value)}
                      >
                        <SelectTrigger id="os-select">
                          <SelectValue placeholder="Select OS" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Operating System</SelectLabel>
                            {isTabletOrganization && (
                              <SelectItem value="Android 13 Tablet">Android 13 Tablet</SelectItem>
                            )}
                            <SelectItem value="Android 14">Android 14</SelectItem>
                            <SelectItem value="Android 15">Android 15</SelectItem>
                            <SelectItem value="iOS 26">iOS 26</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-4 border-t">
                    {!instance ? (
                      <Button
                        className="w-full"
                        onClick={handleCreateAndConnect}
                        disabled={isCreating || organization === "loading"}
                      >
                        {isCreating ? (
                          <>
                            <Loader className="mr-2 h-4 w-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          "Start Instance"
                        )}
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Dialog open={isConnectDialogOpen} onOpenChange={setIsConnectDialogOpen}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full"
                              disabled={instance === "loading" || instance?.status?.state !== "ready"}
                            >
                              <Cable className="mr-2 h-4 w-4" />
                              Connect via CLI
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-[600px]">
                            <DialogHeader>
                              <DialogTitle>Connect via CLI</DialogTitle>
                              <DialogDescription>
                                Use the command below to connect to your instance via the CLI
                              </DialogDescription>
                            </DialogHeader>
                            {instance !== "loading" && (
                              <div className="space-y-4">
                              <div className="p-4 bg-muted rounded-md overflow-auto">
                                <pre className="text-sm">
                                  <code className="whitespace-pre-wrap break-all">
                                    {`lim connect ${selectedOS.startsWith('iOS') ? 'ios' : 'android'} ${instance.metadata.id}`}
                                  </code>
                                </pre>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className="text-sm text-muted-foreground">
                                  If <code className="p-1 rounded-md bg-muted text-xs">lim</code> is not installed, see the instructions in the <Link to="https://limbar.io/docs" target="_blank" className="text-blue-500 hover:underline">docs</Link>.
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    const command = `lim connect ${selectedOS.startsWith('iOS') ? 'ios' : 'android'} ${instance.metadata.id}`;
                                    navigator.clipboard.writeText(command);
                                    toast({
                                      title: "Copied",
                                      description: "Command copied to clipboard",
                                    });
                                  }}
                                >
                                  <Copy className="mr-2 h-3.5 w-3.5" />
                                  Copy
                                </Button>
                              </div>
                            </div>
                            )}
                          </DialogContent>
                        </Dialog>
                        
                        <Button
                          variant="destructive"
                          className="w-full"
                          onClick={handleStopInstance}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Stop Instance
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Streaming Area */}
          <div className="flex-1">
            {!instance ? (
              <Card className="h-full">
                <CardContent className="p-0 h-full">
                  <div className="relative h-full min-h-[80vh] w-full overflow-hidden rounded-lg bg-muted/90">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center space-y-4 p-4">
                        <h2 className="text-2xl font-bold">Playground</h2>
                        <p className="text-muted-foreground">
                          Configure your settings and start a new emulator instance
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full">
                <CardContent className="p-0">
                  <div className="relative h-[90vh] w-full overflow-hidden rounded-lg bg-muted/90">
                    {instance !== "loading" &&
                      instance?.status?.state === "ready" &&
                      instance?.status?.endpointWebSocketUrl && (
                        <RemoteControl
                          url={instance.status.endpointWebSocketUrl}
                          token={instance.status.token}
                        />
                      )}
                    {(instance === "loading" ||
                      instance?.status?.state !== "ready") && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <Loader className="h-8 w-8 animate-spin mx-auto mb-4" />
                          <p className="text-sm text-muted-foreground">Preparing your virtual device instance...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
} 
